import { ToggleGroup } from "radix-ui";
import { useId } from "react";

/**
 * A choice between a few options, all visible at once — where a `<select>` would
 * hide two values behind a click. Radix's single toggle group for the roving
 * focus and arrow keys; it is never left empty, because a deselect would be a
 * value the form cannot send.
 */
export interface ButtonGroupOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly disabled?: boolean;
}

export function ButtonGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  hint,
  error,
}: {
  readonly label: string;
  readonly value: T;
  readonly options: readonly ButtonGroupOption<T>[];
  readonly onChange: (value: T) => void;
  readonly hint?: string;
  readonly error?: string;
}) {
  const labelId = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <span id={labelId} className="text-micro font-medium uppercase text-ink-faint">
        {label}
      </span>
      <ToggleGroup.Root
        type="single"
        aria-labelledby={labelId}
        value={value}
        onValueChange={(next) => {
          if (next !== "") onChange(next as T);
        }}
        className="inline-flex self-start rounded-hairline border border-rule"
      >
        {options.map((option, index) => (
          <ToggleGroup.Item
            key={option.value}
            value={option.value}
            {...(option.disabled === true ? { disabled: true } : {})}
            className={[
              "px-3 py-1.5 text-micro font-semibold uppercase",
              "transition-colors duration-fast ease-swiss",
              index === 0 ? "" : "border-l border-rule",
              "text-ink-muted hover:bg-paper-sunken hover:text-ink",
              "data-[state=on]:bg-ink data-[state=on]:text-paper data-[state=on]:hover:bg-ink",
              "disabled:cursor-not-allowed disabled:text-ink-faint disabled:hover:bg-transparent",
            ].join(" ")}
          >
            {option.label}
          </ToggleGroup.Item>
        ))}
      </ToggleGroup.Root>
      {error === undefined ? null : (
        <span className="text-micro tracking-normal normal-case text-signal">{error}</span>
      )}
      {hint === undefined ? null : (
        <span className="text-micro tracking-normal normal-case text-ink-faint">{hint}</span>
      )}
    </div>
  );
}
